import React, { useCallback, useEffect, useRef, useState } from "react";
import API from "../services/api";
import { localExpiry } from "../utils/accessManagement";
import ServerAuthenticationFields, { certificateFieldNames, certificateUploadFields, serverAuthenticationPayload, serverRequestBody } from "../components/tags/ServerAuthenticationFields";
import { ServerConnectionStatus, ServerLastError } from "../components/tags/ServerConnectionStatus";
import ClientCertificateManager from "../components/tags/ClientCertificateManager";
import "./AddServer.css";

const THEME_STORAGE_KEY = "plcGatewayHomeTheme";

const readPageTheme = () => {
    try {
        const saved = localStorage.getItem(THEME_STORAGE_KEY);
        if (saved === "light" || saved === "dark") {
            return saved;
        }
    } catch (error) {
        console.error("Error reading theme preference:", error);
    }

    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
        return "dark";
    }

    return "light";
};

const messageTone = (text) => {
    const value = String(text || "").toLowerCase();
    if (value.startsWith("failure") || value.includes("fail") || value.includes("please")) {
        return "error";
    }
    if (value.startsWith("testing")) {
        return "pending";
    }
    if (value.startsWith("success") || value.includes("successfully")) {
        return "success";
    }
    return "info";
};

const serverErrorMessage = (error, fallback) => {
    const data = error?.response?.data;
    for (const field of ["auth_mode", "username", "password", ...certificateFieldNames, ...certificateUploadFields, "certificate", "detail"]) {
        if (data?.[field]) return `${field}: ${[].concat(data[field]).join(" ")}`;
    }
    return fallback;
};

const AddServer = ({ canCreate = true, embedded = false, isSuperadmin = false }) => {
    const theme = readPageTheme();
    const [name, setName] = useState("");
    const [address, setAddress] = useState("");
    const [port, setPort] = useState(4840);
    const [description, setDescription] = useState("");
    const [expiresAt, setExpiresAt] = useState("");
    const [authMode, setAuthMode] = useState("anonymous");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [security, setSecurity] = useState({});
    const [clientIdentity, setClientIdentity] = useState(null);
    const [message, setMessage] = useState("");
    const [servers, setServers] = useState([]);
    const [testResult, setTestResult] = useState("");
    const [loadingServers, setLoadingServers] = useState(true);
    const [serversError, setServersError] = useState("");
    const [testing, setTesting] = useState(false);
    const [testingServerId, setTestingServerId] = useState(null);
    const [serverTestResults, setServerTestResults] = useState({});
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState(null);
    const [statusNow, setStatusNow] = useState(Date.now);
    const fetchingServers = useRef(false);

    const fetchServers = useCallback(async () => {
        if (fetchingServers.current) return;
        fetchingServers.current = true;
        try {
            const response = await API.get("/servers/", { timeout: 10000 });
            setServers(response.data);
            setServersError("");
        } catch (error) {
            console.error("Failed to fetch servers:", error);
            setServersError("Unable to load servers.");
        } finally {
            fetchingServers.current = false;
            setLoadingServers(false);
        }
    }, []);

    useEffect(() => {
        fetchServers();
        const interval = window.setInterval(() => {
            setStatusNow(Date.now());
            if (!document.hidden) fetchServers();
        }, 5000);
        return () => window.clearInterval(interval);
    }, [fetchServers]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await API.post("/servers/", serverRequestBody({
                name,
                address,
                port,
                description,
                expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
                ...serverAuthenticationPayload(authMode, username, password, security),
            }));
            setMessage("Server added successfully!");
            setName("");
            setAddress("");
            setPort(4840);
            setDescription("");
            setExpiresAt("");
            setAuthMode("anonymous");
            setUsername("");
            setPassword("");
            setSecurity({});
            fetchServers();
        } catch (error) {
            setMessage(serverErrorMessage(error, "Failed to add server."));
        } finally {
            setSaving(false);
        }
    };

    const beginEdit = (server) => {
        setEditingId(server.id);
        setEditForm({
            name: server.name || "",
            address: server.address || "",
            port: server.port || 4840,
            description: server.description || "",
            expires_at: localExpiry(server.expires_at),
            auth_mode: server.auth_mode || "anonymous",
            username: server.username || "",
            password: "",
            has_password: Boolean(server.has_password),
            ...Object.fromEntries(certificateFieldNames.map((field) => [field, server[field] || ""])),
        });
        setMessage("");
    };

    const updateEdit = (key, value) => {
        setEditForm((current) => ({ ...current, [key]: value }));
        setServerTestResults((current) => ({ ...current, [editingId]: "" }));
    };

    const saveEdit = async (server) => {
        setSaving(true);
        setMessage("");
        try {
            await API.patch(`/servers/${server.id}/`, serverRequestBody({
                name: editForm.name,
                address: editForm.address,
                port: editForm.port,
                description: editForm.description,
                ...serverAuthenticationPayload(editForm.auth_mode, editForm.username, editForm.password, editForm),
            }));
            await API.put(`/access/servers/${server.id}/lifecycle/`, {
                expires_at: editForm.expires_at ? new Date(editForm.expires_at).toISOString() : null,
                is_enabled: server.is_enabled !== false,
            });
            setEditingId(null);
            setEditForm(null);
            setMessage("Server settings saved successfully.");
            await fetchServers();
        } catch (error) {
            setMessage(serverErrorMessage(error, "Failed to save server settings."));
        } finally {
            setSaving(false);
        }
    };

    const toggleArchive = async (server) => {
        const archive = server.is_enabled !== false;
        if (archive && !window.confirm("Archive this server? The collector will stop reading it, but the server, tags, and history will remain.")) return;
        setSaving(true);
        setMessage("");
        try {
            await API.put(`/access/servers/${server.id}/lifecycle/`, {
                expires_at: server.expires_at || null,
                is_enabled: !archive,
            });
            setMessage(archive ? "Server archived. Collector reads are stopped." : "Server restored. Collector reads can resume.");
            await fetchServers();
        } catch (error) {
            setMessage(error?.response?.data?.detail || (archive ? "Failed to archive server." : "Failed to restore server."));
        } finally {
            setSaving(false);
        }
    };

    const handleTestConnection = async (server = null, useDraft = false) => {
        const setResult = server
            ? (result) => setServerTestResults((current) => ({ ...current, [server.id]: result }))
            : setTestResult;
        const draft = server ? (useDraft ? editForm : null) : {
            address, port, auth_mode: authMode, username, password, ...security,
        };
        if (draft && (!draft.address || !draft.port)) {
            setResult("Please provide both address and port.");
            return;
        }
        const payload = {
            ...(server ? { server_id: server.id } : {}),
            ...(draft ? {
                address: draft.address,
                port: draft.port,
                ...serverAuthenticationPayload(draft.auth_mode, draft.username, draft.password, draft),
            } : {}),
        };
        setResult("Testing connection...");
        setTesting(true);
        setTestingServerId(server?.id ?? null);
        try {
            const response = await API.post("/test-connection/", serverRequestBody(payload), { timeout: 25000 });
            setResult(`Success: ${response.data.message} No settings were saved.`);
        } catch (error) {
            setResult(`Failure: ${error.response?.data?.message || serverErrorMessage(error, "Unable to connect")}`);
        } finally {
            setTesting(false);
            setTestingServerId(null);
        }
    };

    const serverRows = Array.isArray(servers) ? servers : [];
    const activeServerRows = serverRows.filter((server) => server.is_enabled !== false);
    const archivedServerRows = isSuperadmin ? serverRows.filter((server) => server.is_enabled === false) : [];

    const renderServerRow = (server) => (
        <React.Fragment key={server.id}>
            <tr>
                <td><strong>{server.name}</strong>{server.description && <small className="server-page__table-description">{server.description}</small>}</td>
                <td className="server-page__endpoint">{server.address}:{server.port}</td>
                <td>{server.auth_mode === "username_cert" ? "Username/password + certificates" : server.auth_mode === "username" ? "Username/password (unencrypted)" : "Anonymous (guest)"}</td>
                <td><ServerConnectionStatus server={server} now={statusNow} /></td>
                <td><ServerLastError server={server} now={statusNow} /></td>
                <td>{server.expires_at ? localExpiry(server.expires_at).replace("T", " ") : "Unlimited"}</td>
                <td>{canCreate && <div className="server-page__row-actions">
                    <button type="button" className="server-page__button" onClick={() => beginEdit(server)}>Edit</button>
                    <button type="button" className="server-page__button" disabled={saving || testing} aria-label={`Test connection to ${server.name}`} onClick={() => handleTestConnection(server)}>{testing && testingServerId === server.id ? "Testing…" : "Test PLC"}</button>
                    <button type="button" className="server-page__button server-page__button--danger" disabled={saving} onClick={() => toggleArchive(server)}>Archive</button>
                </div>}{serverTestResults[server.id] && <p className={`server-page__banner is-${messageTone(serverTestResults[server.id])}`} role="status">{serverTestResults[server.id]}</p>}</td>
            </tr>
            {editingId === server.id && editForm ? <tr><td colSpan="7">
                <div className="server-page__edit">
                    <div className="server-page__grid">
                        <div className="server-page__field"><label>Name</label><input value={editForm.name} onChange={(event) => updateEdit("name", event.target.value)} /></div>
                        <div className="server-page__field"><label>Address</label><input value={editForm.address} onChange={(event) => updateEdit("address", event.target.value)} /></div>
                        <div className="server-page__field"><label>Port</label><input type="number" value={editForm.port} onChange={(event) => updateEdit("port", event.target.value)} /></div>
                        <ServerAuthenticationFields prefix={`edit-server-${server.id}`} mode={editForm.auth_mode} username={editForm.username} password={editForm.password} security={editForm} hasPassword={editForm.has_password} onChange={updateEdit} disabled={saving || testing} clientIdentity={isSuperadmin ? clientIdentity : null} />
                        <div className="server-page__field"><label>Expiry</label><input type="datetime-local" step="1" value={editForm.expires_at} onChange={(event) => updateEdit("expires_at", event.target.value)} /><small>{editForm.expires_at ? "Scheduled expiry" : "Unlimited"}</small></div>
                        <div className="server-page__field server-page__field--wide"><label>Description</label><textarea rows="3" value={editForm.description} onChange={(event) => updateEdit("description", event.target.value)} /></div>
                    </div>
                    <div className="server-page__actions">
                        <button type="button" className="server-page__button server-page__button--primary" disabled={saving} onClick={() => saveEdit(server)}>Save</button>
                        <button type="button" className="server-page__button" disabled={saving || testing} aria-label={`Test server details for ${server.name}`} onClick={() => handleTestConnection(server, true)}>Test PLC</button>
                        <button type="button" className="server-page__button" disabled={saving} onClick={() => { setEditingId(null); setEditForm(null); }}>Cancel</button>
                    </div>
                    <small>Test PLC checks these fields without saving changes. A blank password uses the stored password.</small>
                </div>
            </td></tr> : null}
        </React.Fragment>
    );

    return (
        <div className={`server-page${embedded ? " server-page--embedded" : ""}`} data-theme={theme}>
            {!embedded ? <header className="server-page__top">
                <div>
                    <p className="server-page__eyebrow">OPC UA servers</p>
                    <h1>{canCreate ? "Add Server" : "Assigned servers"}</h1>
                    <p className="server-page__subtitle">
                        {canCreate ? "Save a server endpoint, or test its selected authentication when you choose to. A connection test does not save changes." : "Servers assigned to your administrator account."}
                    </p>
                </div>
            </header> : null}

            {isSuperadmin && <ClientCertificateManager onIdentityChange={setClientIdentity} />}

            {canCreate && <section className="server-page__card" aria-labelledby="add-server-heading">
                <div className="server-page__card-head">
                    <h2 id="add-server-heading">Server details</h2>
                    <p>Port starts at 4840. A server is created only when you submit this form.</p>
                </div>

                <form className="server-page__form" onSubmit={handleSubmit}>
                    <div className="server-page__grid">
                        <div className="server-page__field">
                            <label htmlFor="server-name">Server Name</label>
                            <input
                                id="server-name"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                        </div>
                        <div className="server-page__field">
                            <label htmlFor="server-address">Server Address</label>
                            <input
                                id="server-address"
                                type="text"
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                                required
                                spellCheck="false"
                                autoComplete="off"
                            />
                        </div>
                        <div className="server-page__field">
                            <label htmlFor="server-port">Server Port</label>
                            <input
                                id="server-port"
                                type="number"
                                value={port}
                                onChange={(e) => setPort(e.target.value)}
                                required
                            />
                        </div>
                        <div className="server-page__field server-page__field--wide">
                            <label htmlFor="server-description">Description</label>
                            <textarea
                                id="server-description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                rows={4}
                            />
                        </div>
                        <ServerAuthenticationFields prefix="new-server" mode={authMode} username={username} password={password} security={security} clientIdentity={isSuperadmin ? clientIdentity : null} disabled={saving || testing} onChange={(key, value) => {
                            setTestResult("");
                            if (key === "auth_mode") { setAuthMode(value); setTestResult(""); if (value === "anonymous") { setUsername(""); setPassword(""); } }
                            if (key === "username") setUsername(value);
                            if (key === "password") setPassword(value);
                            if ([...certificateFieldNames, ...certificateUploadFields].includes(key)) setSecurity((current) => ({ ...current, [key]: value }));
                        }} />
                        <div className="server-page__field">
                            <label htmlFor="server-expiry">Expiry</label>
                            <input
                                id="server-expiry"
                                type="datetime-local"
                                step="1"
                                value={expiresAt}
                                onChange={(event) => setExpiresAt(event.target.value)}
                            />
                            <small>{expiresAt ? "Scheduled expiry" : "Unlimited"}</small>
                        </div>
                    </div>
                    <div className="server-page__actions">
                        <button className="server-page__button server-page__button--primary" type="submit" disabled={saving}>
                            {saving ? "Adding…" : "Add Server"}
                        </button>
                    </div>
                </form>

                {message && (
                    <p className={`server-page__banner is-${messageTone(message)}`} role="status">
                        {message}
                    </p>
                )}

                <div className="server-page__test">
                    <button
                        className="server-page__button"
                        type="button"
                        onClick={() => handleTestConnection()}
                        aria-label="Test new server connection"
                        disabled={testing || saving}
                    >
                        {testing && testingServerId === null ? "Testing…" : "Test PLC"}
                    </button>
                    {testResult && (
                        <p className={`server-page__banner is-${messageTone(testResult)}`} role="status">
                            {testResult}
                        </p>
                    )}
                </div>
            </section>}

            <section className="server-page__card" aria-labelledby="existing-servers-heading">
                <div className="server-page__card-head">
                    <h2 id="existing-servers-heading">Existing Servers</h2>
                    <p>{loadingServers ? "Loading servers…" : `${activeServerRows.length} active • Connection status refreshes every 5 seconds`}</p>
                </div>

                {serversError && <p className="server-page__banner is-error" role="alert">{serversError}</p>}

                {loadingServers ? (
                    <p className="server-page__empty">Loading servers…</p>
                ) : activeServerRows.length === 0 ? (
                    <p className="server-page__empty">No servers are stored yet.</p>
                ) : (
                    <div className="server-page__table-wrap"><table className="server-page__archive-table server-page__servers-table">
                        <thead><tr><th>Name</th><th>Endpoint</th><th>Authentication</th><th>Connection</th><th>Latest error</th><th>Expiry</th><th>Actions</th></tr></thead>
                        <tbody>{activeServerRows.map(renderServerRow)}</tbody>
                    </table></div>
                )}
            </section>

            {isSuperadmin ? <section className="server-page__card" aria-labelledby="archived-servers-heading">
                <div className="server-page__card-head">
                    <h2 id="archived-servers-heading">Archived Servers</h2>
                    <p>{loadingServers ? "Loading servers…" : `${archivedServerRows.length} archived`}</p>
                </div>
                {loadingServers ? <p className="server-page__empty">Loading servers…</p> : archivedServerRows.length === 0 ? (
                    <p className="server-page__empty">No archived servers.</p>
                ) : <div className="server-page__table-wrap"><table className="server-page__archive-table">
                    <thead><tr><th>Name</th><th>Endpoint</th><th>Connection</th><th>Latest error</th><th>Expiry</th><th>Actions</th></tr></thead>
                    <tbody>{archivedServerRows.map((server) => (
                        <React.Fragment key={server.id}>
                            <tr>
                                <td>{server.name}</td>
                                <td className="server-page__endpoint">{server.address}:{server.port}</td>
                                <td><ServerConnectionStatus server={server} now={statusNow} /></td>
                                <td><ServerLastError server={server} now={statusNow} /></td>
                                <td>{server.expires_at ? localExpiry(server.expires_at).replace("T", " ") : "Unlimited"}</td>
                                <td><div className="server-page__row-actions">
                                    <button type="button" className="server-page__button" onClick={() => beginEdit(server)}>Edit</button>
                                    <button type="button" className="server-page__button server-page__button--primary" disabled={saving} onClick={() => toggleArchive(server)}>Restore</button>
                                </div></td>
                            </tr>
                            {editingId === server.id && editForm ? <tr><td colSpan="6">
                                <div className="server-page__edit">
                                    <div className="server-page__grid">
                                        <div className="server-page__field"><label>Name</label><input value={editForm.name} onChange={(event) => updateEdit("name", event.target.value)} /></div>
                                        <div className="server-page__field"><label>Address</label><input value={editForm.address} onChange={(event) => updateEdit("address", event.target.value)} /></div>
                                        <div className="server-page__field"><label>Port</label><input type="number" value={editForm.port} onChange={(event) => updateEdit("port", event.target.value)} /></div>
                                        <ServerAuthenticationFields prefix={`archive-server-${server.id}`} mode={editForm.auth_mode} username={editForm.username} password={editForm.password} security={editForm} hasPassword={editForm.has_password} onChange={updateEdit} disabled={saving} clientIdentity={isSuperadmin ? clientIdentity : null} />
                                        <div className="server-page__field"><label>Expiry</label><input type="datetime-local" step="1" value={editForm.expires_at} onChange={(event) => updateEdit("expires_at", event.target.value)} /><small>{editForm.expires_at ? "Scheduled expiry" : "Unlimited"}</small></div>
                                        <div className="server-page__field server-page__field--wide"><label>Description</label><textarea rows="3" value={editForm.description} onChange={(event) => updateEdit("description", event.target.value)} /></div>
                                    </div>
                                    <div className="server-page__actions">
                                        <button type="button" className="server-page__button server-page__button--primary" disabled={saving} onClick={() => saveEdit(server)}>Save</button>
                                        <button type="button" className="server-page__button" disabled={saving} onClick={() => { setEditingId(null); setEditForm(null); }}>Cancel</button>
                                    </div>
                                </div>
                            </td></tr> : null}
                        </React.Fragment>
                    ))}</tbody>
                </table></div>}
            </section> : null}
        </div>
    );
};

export default AddServer;
